import { createFileRoute, notFound } from "@tanstack/react-router";
import { Truck } from "lucide-react";
import { PageHeader } from "@/components/app/page-header";
import { StatusChip } from "@/components/app/status-chip";
import { StepIndicator } from "@/components/app/step-indicator";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { DOC_STATUSES } from "@/features/documents/status";

/** DEV-only kitchen sink: every token, tone and control in light AND dark side by side (contrast audit). */
export const Route = createFileRoute("/_app/dev/tokens")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  component: Tokens,
});

const TONES: BadgeTone[] = [
  "neutral",
  "success",
  "warning",
  "danger",
  "info",
  "progress",
  "manual",
  "loss",
  "gain",
  "primary",
];
const SWATCHES = [
  "surface",
  "surface-raised",
  "surface-sunken",
  "surface-header",
  "border",
  "border-strong",
  "ink",
  "ink-secondary",
  "ink-muted",
  "brand",
  "brand-strong",
  "brand-soft",
  "brand-border",
  "success",
  "success-soft",
  "warning",
  "warning-soft",
  "danger",
  "danger-soft",
  "progress",
  "progress-soft",
  "neutral",
  "neutral-soft",
  "manual",
  "manual-soft",
  "loss",
  "gain",
  "rail",
];

function Sink() {
  return (
    <div className="space-y-4 bg-surface p-4 text-ink">
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Swatches
        </h3>
        <div className="grid grid-cols-6 gap-2 text-[10px]">
          {SWATCHES.map((s) => (
            <div key={s} className="space-y-1">
              <div
                className="h-8 rounded-md border border-border"
                style={{ background: `var(--${s})` }} // raw palette vars: theme vars are tree-shaken unless a utility uses them
              />
              <div className="truncate font-mono">{s}</div>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Badges — soft / solid / outline
        </h3>
        {(["soft", "solid", "outline"] as const).map((v) => (
          <div key={v} className="mb-1.5 flex flex-wrap gap-1.5">
            {TONES.map((t) => (
              <Badge key={t} tone={t} variant={v} dot>
                {t}
              </Badge>
            ))}
          </div>
        ))}
      </section>
      <section className="flex flex-wrap gap-1.5">
        {DOC_STATUSES.map((s) => (
          <StatusChip key={s} status={s} />
        ))}
      </section>
      <section className="flex flex-wrap items-center gap-2">
        <Button>Primary</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="outline">Outline</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="danger">Danger</Button>
        <Button variant="link">Link</Button>
        <Button loading>Saving</Button>
        <Button disabled>Disabled</Button>
      </section>
      <section className="grid grid-cols-3 gap-2">
        <Input placeholder="Input" />
        <Input placeholder="Invalid" invalid />
        <Select>
          <option>Select</option>
        </Select>
      </section>
      <section className="space-y-2">
        <Callout tone="info" title="Info">
          Body text on the info tint.
        </Callout>
        <Callout tone="warning" title="Warning">
          Body text on the warning tint.
        </Callout>
        <Callout tone="danger" title="Danger">
          Body text on the danger tint.
        </Callout>
        <Callout tone="success" title="Success">
          Body text on the success tint.
        </Callout>
      </section>
      <section className="rounded-xl border border-border bg-surface-raised px-3 py-2">
        <StepIndicator
          steps={[
            { key: "a", label: "Done", state: "done", hint: "GPI/D/Y/1" },
            {
              key: "b",
              label: "Current",
              state: "current",
              chips: [{ label: "2/3 lots", tone: "progress" }],
            },
            {
              key: "c",
              label: "Blocked",
              state: "blocked",
              chips: [{ label: "LR Number", tone: "danger" }],
            },
            { key: "d", label: "Skipped", state: "skipped" },
            { key: "e", label: "Todo", state: "todo" },
          ]}
        />
      </section>
      <section className="grid grid-cols-3 gap-2 text-sm">
        <span className="text-loss">−2.200 loss</span>
        <span className="text-gain">+0.500 gain</span>
        <span className="text-primary-text">primary link text</span>
      </section>
    </div>
  );
}

function Tokens() {
  return (
    <>
      <PageHeader
        title="Design tokens"
        description="Development only — every tone and control rendered in light and dark side by side."
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-4 w-4" /> Light
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <div className="rounded-b-xl">
              <Sink />
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Dark</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <div className="dark rounded-b-xl">
              <Sink />
            </div>
          </CardBody>
        </Card>
      </div>
    </>
  );
}
